import React from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { MetricCard } from '@/components/MetricCard'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { RoleDetailSkeleton } from '@/components/common/RoleDetailSkeleton'
import { Badge } from '@/components/ui/Badge'
import { useRole } from '@/hooks/useWorkforce'
import {
  Briefcase,
  ArrowLeft,
  Edit,
  Users,
  DollarSign,
  Building2,
  RefreshCw,
  Star,
  CheckCircle,
  AlertCircle
} from 'lucide-react'

function RoleDetailContent() {
  const params = useParams({ from: '/_auth/workforce/roles/$roleId/' })
  const roleId = params.roleId

  const { role, loading, isFetching, error, refetch } = useRole(roleId)

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount)
  }

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'junior':
        return 'bg-blue-500/20 text-blue-400'
      case 'mid':
        return 'bg-green-500/20 text-green-400'
      case 'senior':
        return 'bg-purple-500/20 text-purple-400'
      case 'lead':
        return 'bg-yellow-500/20 text-yellow-400'
      case 'manager':
        return 'bg-orange-500/20 text-orange-400'
      case 'director':
        return 'bg-red-500/20 text-red-400'
      case 'executive':
        return 'bg-brand-primary/20 text-brand-primary'
      default:
        return 'bg-brand-gray/20 text-brand-gray'
    }
  }

  if (loading) {
    return <RoleDetailSkeleton />
  }

  if (error || !role) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error || 'Cargo não encontrado'}</p>
        <Link to="/workforce/roles">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Cargos
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/workforce/roles">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">
                {role.name}
              </h1>
              {role.level && (
                <Badge className={getLevelColor(role.level)}>
                  <span className="capitalize">{role.level}</span>
                </Badge>
              )}
            </div>
            {role.description && (
              <p className="font-body text-brand-gray mt-2">
                {role.description}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isFetching && (
            <RefreshCw className="w-4 h-4 text-brand-gray animate-spin" />
          )}
          <Link to={`/workforce/roles/${roleId}/edit` as any}>
            <Button size="sm">
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-grid lg:grid-cols-3">
        <MetricCard
          title="Colaboradores"
          value={role.employees?.length || 0}
          icon={Users}
          trend="neutral"
        />
        <MetricCard
          title="Faixa Salarial"
          value={role.salary ? `${formatCurrency(role.salary.min)} - ${formatCurrency(role.salary.max)}` : 'N/A'}
          icon={DollarSign}
          trend="neutral"
        />
        <MetricCard
          title="Departamento"
          value={role.department?.name || 'N/A'}
          icon={Building2}
          trend="neutral"
        />
      </div>

      {/* Role Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Responsibilities */}
        {role.responsibilities && role.responsibilities.length > 0 && (
          <Card className="p-6">
            <h2 className="font-display text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-brand-primary" />
              Responsabilidades
            </h2>
            <div className="space-y-3">
              {role.responsibilities.map((resp, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-brand-primary rounded-full mt-2 flex-shrink-0" />
                  <span className="text-white">{resp}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Requirements */}
        {role.requirements && role.requirements.length > 0 && (
          <Card className="p-6">
            <h2 className="font-display text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-brand-primary" />
              Requisitos
            </h2>
            <div className="space-y-3">
              {role.requirements.map((req, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-brand-primary rounded-full mt-2 flex-shrink-0" />
                  <span className="text-white">{req}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Salary Details */}
      {role.salary && (
        <Card className="p-6">
          <h2 className="font-display text-xl font-semibold text-white mb-6">
            Detalhes Salariais
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <div className="text-sm text-brand-gray">Salário Mínimo</div>
              <div className="font-semibold text-white text-xl">{formatCurrency(role.salary.min)}</div>
            </div>
            <div className="space-y-2">
              <div className="text-sm text-brand-gray">Salário Máximo</div>
              <div className="font-semibold text-white text-xl">{formatCurrency(role.salary.max)}</div>
            </div>
            <div className="space-y-2">
              <div className="text-sm text-brand-gray">Moeda</div>
              <div className="font-semibold text-white text-xl">{role.salary.currency}</div>
            </div>
          </div>
        </Card>
      )}

      {/* Employees with this role */}
      {role.employees && role.employees.length > 0 && (
        <Card className="p-6">
          <h2 className="font-display text-xl font-semibold text-white mb-6">
            Colaboradores neste Cargo
          </h2>
          <div className="space-y-3">
            {role.employees.map((employee) => (
              <Link
                key={employee.employeeId}
                to={`/workforce/employees/${employee.employeeId}` as any}
                className="flex items-center gap-3 p-3 bg-brand-dark/50 rounded-monumenta hover:bg-brand-dark transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-brand-primary/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-brand-primary" />
                </div>
                <div>
                  <div className="font-semibold text-white">{employee.personalInfo.name}</div>
                  <div className="text-sm text-brand-gray">{employee.email}</div>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

// Apply RBAC protection
export default withPageProtection(RoleDetailContent, PAGES.WORKFORCE_ROLES)
